import type { UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { LanguageCode } from '~/shared/internationalization';

import type { AutomationDetailResponse } from '../../data-contract/automation';
import { useApplicationFetchers } from '../../fetchers/use-application-fetchers';
import { jobArchitectureKeys } from '../../job-architecture-v2';

const STALE_TIME_MS = 30_000;

/** Everything a point does not carry: per-task rows, released ranges, ancestors. One item per call. */
export const useAutomationDetail = ({
    acceptLanguage,
    architectureId,
    itemId,
}: {
    readonly acceptLanguage: LanguageCode;
    readonly architectureId: string | undefined;
    readonly itemId: string | undefined;
}): UseQueryResult<AutomationDetailResponse, Error> => {
    const { fetchAutomationDetail } = useApplicationFetchers();

    return useQuery({
        enabled: architectureId !== undefined && itemId !== undefined,
        queryFn: async ({ signal }) => {
            const result = await fetchAutomationDetail({
                acceptLanguage,
                architectureId: architectureId ?? '',
                itemId: itemId ?? '',
                signal,
            });

            if (result.isErr) {
                throw result.error;
            }

            return result.value;
        },
        queryKey: jobArchitectureKeys.automationDetail(architectureId ?? '', itemId ?? '', acceptLanguage),
        staleTime: STALE_TIME_MS,
        throwOnError: false,
    });
};
